import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Conversation } from "../../../../model/integration/dto/meta/MetaConversation";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import {
  byRecency,
  chatNeedsAttention,
  chatRecency,
} from "../../utils/integration-utils";

const SEARCH_DEBOUNCE_MS = 300;
/** How close to the end of the list, in pixels, the next page is asked for */
const LOAD_MORE_DISTANCE = 240;

type Params = {
  activeChatId: string;
  setActiveChatId: (chatId: string) => void;
  /** The scrolling list, so reaching its end can ask for the next page */
  listRef: RefObject<HTMLDivElement | null>;
};

/** What the inbox's chats list shows, and how it gets the rest.
 *
 * The list only holds the pages loaded so far, so a search, the attention
 * filter or a tag filter is put to the server, which answers across the whole inbox. A chat the
 * open link names but the pages have not reached is fetched by id.
 */
export const useWAChatsView = ({
  activeChatId,
  setActiveChatId,
  listRef,
}: Params) => {
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId);
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const chatsList = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsList,
  );
  const listLoading = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsListLoading,
  );
  const listLoaded = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsListLoaded,
  );
  const listNextCursor = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsListNextCursor,
  );
  const listLoadingMore = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsListLoadingMore,
  );
  const listReachedAt = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsListReachedAt,
  );
  const view = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsView,
  );
  const attentionCount = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsAttentionCount,
  );

  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(false);
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [resolvingChat, setResolvingChat] = useState(false);
  const fetchedChatRef = useRef("");
  const failedCursorRef = useRef<string | undefined>(undefined);

  const integrationId = integration?._id;
  const phoneNumberId = integration?.config?.phoneNumberId;
  const activeChatLoaded = (chatsList || []).some(
    (chat) => chat._id === activeChatId,
  );

  // The open chat stays in the attention queue, otherwise answering the chat
  // you are reading makes it vanish from under you the moment it resolves. A
  // tag filter gets no such exception: it shows the chats carrying the tag.
  const visibleChats = useMemo(() => {
    // Only as far down as the pages reach, so the list never shows a chat with
    // a gap above it that paging has yet to fill
    if (!view) {
      return listReachedAt === null
        ? chatsList
        : (chatsList || []).filter(
            (chat) =>
              chatRecency(chat) >= listReachedAt || chat._id === activeChatId,
          );
    }

    const byId = new Map((chatsList || []).map((chat) => [chat._id, chat]));
    const found = view.ids
      .map((id) => byId.get(id))
      .filter((chat): chat is Conversation => Boolean(chat));

    if (!onlyNeedsAttention && !filterTagIds.length) {
      return found;
    }

    // Chats that turned up live, or were tagged, while a filter is on belong
    // in it too - and one whose tag was just taken off drops out
    const live = debouncedSearch
      ? []
      : (chatsList || []).filter((chat) => !view.ids.includes(chat._id));

    const matchesTags = (chat: Conversation) =>
      !filterTagIds.length ||
      Boolean(chat.tagIds?.some((id) => filterTagIds.includes(id)));

    const matchesAttention = (chat: Conversation) =>
      !onlyNeedsAttention ||
      chatNeedsAttention(chat) ||
      chat._id === activeChatId;

    return [...found, ...live]
      .filter((chat) => matchesTags(chat) && matchesAttention(chat))
      .sort(byRecency);
  }, [
    view,
    chatsList,
    listReachedAt,
    onlyNeedsAttention,
    filterTagIds,
    debouncedSearch,
    activeChatId,
  ]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS,
    );

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!userId || !integrationId || !phoneNumberId) {
      return;
    }

    if (!debouncedSearch && !onlyNeedsAttention && !filterTagIds.length) {
      dispatch(integrationActions.metaClearChatsView());
      return;
    }

    dispatch(
      integrationActions.getWhatsAppMetaChatsView({
        userId,
        integrationId,
        phoneNumberId,
        search: debouncedSearch || undefined,
        needsAttention: onlyNeedsAttention || undefined,
        tagIds: filterTagIds.length ? filterTagIds : undefined,
      }),
    );
  }, [
    dispatch,
    userId,
    integrationId,
    phoneNumberId,
    debouncedSearch,
    onlyNeedsAttention,
    filterTagIds,
  ]);

  // The chip is hidden at zero, so leaving the filter on would strand someone on
  // an empty list with no control left on screen to switch it off
  useEffect(() => {
    if (!attentionCount && onlyNeedsAttention) {
      setOnlyNeedsAttention(false);
    }
  }, [attentionCount, onlyNeedsAttention]);

  // A link, a reload or a notification can name a chat the first page does not
  // reach. It is fetched by id rather than treated as missing.
  useEffect(() => {
    if (
      !activeChatId ||
      activeChatLoaded ||
      !listLoaded ||
      !userId ||
      !integrationId ||
      fetchedChatRef.current === activeChatId
    ) {
      return;
    }

    fetchedChatRef.current = activeChatId;
    setResolvingChat(true);

    dispatch(
      integrationActions.getWhatsAppMetaChat({
        userId,
        integrationId,
        conversationId: activeChatId,
      }),
    )
      .unwrap()
      .catch(() => setActiveChatId(""))
      .finally(() => setResolvingChat(false));
  }, [
    dispatch,
    userId,
    integrationId,
    activeChatId,
    activeChatLoaded,
    listLoaded,
    setActiveChatId,
  ]);

  const nextCursor = view ? view.nextCursor : listNextCursor;
  const busy = view
    ? view.loading || view.loadingMore
    : listLoading || listLoadingMore;

  const loadMoreChats = useCallback(() => {
    if (!userId || !integrationId || !phoneNumberId || !nextCursor || busy) {
      return;
    }

    const remember = () => {
      failedCursorRef.current = nextCursor;
    };

    if (view) {
      dispatch(
        integrationActions.getWhatsAppMetaChatsView({
          userId,
          integrationId,
          phoneNumberId,
          search: debouncedSearch || undefined,
          needsAttention: onlyNeedsAttention || undefined,
          tagIds: filterTagIds.length ? filterTagIds : undefined,
          cursor: nextCursor,
        }),
      )
        .unwrap()
        .catch(remember);
      return;
    }

    dispatch(
      integrationActions.loadMoreWhatsAppMetaChats({
        userId,
        integrationId,
        phoneNumberId,
        cursor: nextCursor,
      }),
    )
      .unwrap()
      .catch(remember);
  }, [
    dispatch,
    userId,
    integrationId,
    phoneNumberId,
    view,
    nextCursor,
    busy,
    debouncedSearch,
    onlyNeedsAttention,
    filterTagIds,
  ]);

  const atEnd = useCallback(() => {
    const list = listRef.current;

    return Boolean(
      list &&
        list.scrollHeight - list.scrollTop - list.clientHeight <
          LOAD_MORE_DISTANCE,
    );
  }, [listRef]);

  const onListScroll = useCallback(() => {
    if (atEnd()) {
      loadMoreChats();
    }
  }, [atEnd, loadMoreChats]);

  // Reaching the end while something else was loading asks for nothing, and at
  // the end there is no further scroll to ask again - the list just stopped.
  // So once loading settles, the end is checked again. Not after a failure,
  // which would retry in a loop; scrolling asks again instead.
  useEffect(() => {
    if (nextCursor !== failedCursorRef.current && atEnd()) {
      loadMoreChats();
    }
  }, [atEnd, loadMoreChats, nextCursor, visibleChats?.length]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchText("");
    setDebouncedSearch("");
  }, []);

  const toggleSearch = useCallback(() => {
    if (searchOpen) {
      closeSearch();
      return;
    }

    setSearchOpen(true);
  }, [searchOpen, closeSearch]);

  const toggleNeedsAttention = useCallback(
    () => setOnlyNeedsAttention((on) => !on),
    [],
  );

  const toggleFilterTag = useCallback(
    (tagId: string) =>
      setFilterTagIds((ids) =>
        ids.includes(tagId)
          ? ids.filter((id) => id !== tagId)
          : [...ids, tagId],
      ),
    [],
  );

  const clearFilterTags = useCallback(() => setFilterTagIds([]), []);

  return {
    visibleChats,
    /** Nothing to show yet: the first page, or the answer to a search */
    loading: (!visibleChats?.length && listLoading) || Boolean(view?.loading),
    loadingMore: listLoadingMore || Boolean(view?.loadingMore),
    /** What is on screen is the answer to a search or a filter */
    filtered: Boolean(view),
    onListScroll,
    /** The chat in the link is being fetched */
    resolvingChat,
    attentionCount,
    onlyNeedsAttention,
    toggleNeedsAttention,
    filterTagIds,
    toggleFilterTag,
    clearFilterTags,
    searchOpen,
    searchText,
    setSearchText,
    toggleSearch,
    closeSearch,
  };
};
